import { breakpoints, Heading, Inline, Stack } from '@etg/wings';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Notification, variants } from '@eti/components';
import ShareTrip from '../../../../../../widgets/share-trip/ShareTrip';
import { SaveTrip } from '../saveTrip/SaveTrip';
import type {
  RebookingTripBoundsType,
  RebookingTripTypeType,
} from '../../../../../change-trip/models';
import type { BoundsType } from '../../../../models';
import type { TripType as CustomTripType } from '../model';
import { isTripWithDifferentOriginOrDestination } from '../../../../../../widgets/trip-details/utils/originDestinationUtil';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import { transformSegments } from '../../../../../order-details/components/overview/itinerary/drawer/utils/utils';
import { SegmentType } from '../../../../../../constants/segmentTypesConstants';
import { SELF_TRANSFER } from '../../../../../../constants/eventSegmentTypes';
import ItineraryDetailsLocationTime from './ItineraryDetailsLocationTime';
import { getFinalSegments } from './utils';

const headlineStyles = css`
  justify-content: space-between;
`;

const saveShareTripStyles = css`
  gap: 14px;
`;

interface ItineraryContainerProps {
  bounds: BoundsType | RebookingTripBoundsType;
  isSponsoredTrip?: boolean;
  isVI?: boolean;
  priceDisplayText: string;
  shareableUrl?: string;
  shouldShowSaveTrip?: boolean;
  shouldShowShareTrip?: boolean;
  totalPrice?: string;
  type: CustomTripType | RebookingTripTypeType;
}

export const ItineraryInfo = ({
  bounds,
  isSponsoredTrip,
  isVI,
  priceDisplayText,
  shareableUrl,
  shouldShowSaveTrip = false,
  shouldShowShareTrip = false,
  totalPrice,
  type,
}: ItineraryContainerProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { currentSearchData } = useSearch();
  const segments = getFinalSegments(bounds);
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);
  const isDifferentAirportNotificationEnabled = p('Result.DifferentAirportNotification.Enabled');
  const tripHasDifferentOriginOrDestination =
    !isChangeTripPage &&
    isTripWithDifferentOriginOrDestination(currentSearchData?.routes ?? [], bounds);
  const shouldShowDifferentAirportBadge =
    tripHasDifferentOriginOrDestination && isDifferentAirportNotificationEnabled;

  const getSelfTransferPlaces = (segmentIndex: number) => {
    const places: string[] = [];
    transformSegments(bounds[segmentIndex].segments).forEach((segment) => {
      if (
        segment.__typename === SegmentType.EVENT_SEGMENT &&
        segment.types?.includes(SELF_TRANSFER)
      ) {
        places.push(segment.previousDestination);
      }
    });

    return places;
  };

  return (
    <Stack data-testid="itinerary-info-wrapper">
      <Inline className={headlineStyles}>
        <Heading level={3}>{t('Result.Page.Drawer.Itinerary.Title')}</Heading>
        <Inline className={saveShareTripStyles}>
          {shouldShowSaveTrip && Boolean(totalPrice) && (
            <SaveTrip
              isSponsoredTrip={isSponsoredTrip}
              isVI={isVI}
              priceDisplayText={priceDisplayText}
              totalPrice={totalPrice!!}
              type={type}
            />
          )}
          {shouldShowShareTrip && shareableUrl && (
            <ShareTrip
              bounds={bounds}
              breakpoint={breakpoints._0}
              priceDisplayText={priceDisplayText}
              shareableUrl={shareableUrl}
              totalPrice={totalPrice?.toString()}
              type={type}
            />
          )}
        </Inline>
      </Inline>
      <div>
        {segments.map((segment, index) => (
          <ItineraryDetailsLocationTime
            arrivalCityCode={segment.destination?.cityCode}
            arrivalCode={segment.destination?.code ?? ''}
            arrivalDate={segment.arrivedAt as string}
            boundNumber={segment.boundNumber}
            brandedFareInformation={segment.brandedFareInformation}
            cabinClass={segment.cabinClassName}
            carriers={segment.allMarketingCarriers}
            departureCityCode={segment.origin?.cityCode}
            departureCode={segment.origin?.code ?? ''}
            departureDate={segment.departuredAt as string}
            duration={segment.totalDuration}
            isSelfTransfer={segment.isSelfTransfer}
            key={segment.segmentId}
            marketingCarrier={segment.marketingCarrier}
            numberOfTechnicalStops={segment.totalNumberOfTechnicalStops}
            selfTransferPlaces={getSelfTransferPlaces(index)}
            stopoverType={segment.stopoverType}
            stops={segment.stops}
            trainSegment={segment.trainSegment}
          />
        ))}
      </div>
      {shouldShowDifferentAirportBadge && (
        <Notification
          dataTestId="nearby-airports-drawer-notification"
          title={t('Result.TripDetails.DifferentAirport.Header')}
          variant={variants.INFORMATION}
        >
          {t('Result.TripDetails.DifferentAirport.Description')}
        </Notification>
      )}
    </Stack>
  );
};
